import { getPrazoEnvioInfo } from './prazo-envio-utils';

import type { ReadRelatorioAtividadeHistoricoDto } from '@/api.types';
import { RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.service';

export interface RelatorioHistoricoEvento {
  data: string;
  label: string;
  detalhe?: string;
  badge?: {
    text: string;
    title?: string;
  };
}

function formatarDataIso(value: string | Date | undefined | null): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

function formatarPrazoLabel(value: unknown): string | undefined {
  if (!value) return undefined;
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function detalhePorEvento(item: ReadRelatorioAtividadeHistoricoDto): string | undefined {
  const partes: string[] = [];
  if (item.usuarioNome) {
    partes.push(`Por: ${item.usuarioNome}`);
  }
  if (item.justificativa) {
    partes.push(`Justificativa: ${item.justificativa}`);
  }

  switch (item.evento) {
    case 'envio_diretoria':
      partes.push(
        item.payload?.reenvio
          ? 'Reenvio após correção solicitada pela Diretoria regional'
          : 'Encaminhado para análise e aprovação da diretoria regional',
      );
      break;
    case 'aprovacao_diretoria':
      partes.push('Encaminhado à Corregedoria para aval final');
      break;
    case 'reenvio_direto_corregedoria':
      partes.push(
        'Após correções solicitadas pela Corregedoria (sem novo trâmite obrigatório pelo diretor regional)',
      );
      break;
    default:
      break;
  }

  if (item.payload?.foraPrazo) {
    const prazoLabel = formatarPrazoLabel(item.payload.prazoFinal);
    partes.push(prazoLabel ? `Fora do prazo (prazo final: ${prazoLabel})` : 'Fora do prazo');
  }

  return partes.length > 0 ? partes.join(' · ') : undefined;
}

export function mapRelatorioHistoricoPersistido(
  itens: ReadRelatorioAtividadeHistoricoDto[],
): RelatorioHistoricoEvento[] {
  return [...itens]
    .sort((a, b) => {
      const byDate = formatarDataIso(a.criadoEm).localeCompare(formatarDataIso(b.criadoEm));
      if (byDate !== 0) return byDate;
      return Number(a.id) - Number(b.id);
    })
    .map((item) => {
      const prazoLabel = formatarPrazoLabel(item.payload?.prazoFinal);
      return {
        data: formatarDataIso(item.criadoEm),
        label: item.label,
        detalhe: detalhePorEvento(item),
        badge: item.payload?.foraPrazo
          ? {
              text: 'Fora do prazo',
              title: prazoLabel ? `Prazo final: ${prazoLabel}` : undefined,
            }
          : undefined,
      };
    });
}

/**
 * Fallback a partir do snapshot atual do relatório.
 * Ciclos já sobrescritos no backend não aparecem.
 */
export function buildRelatorioHistoricoEventos(relatorio: RelatorioAtividadeDto): RelatorioHistoricoEvento[] {
  const eventos: RelatorioHistoricoEvento[] = [];
  const fmt = (d: string | undefined) => (d ? new Date(d).toISOString() : '');

  if (relatorio.dataCriacao) {
    eventos.push({ data: fmt(relatorio.dataCriacao), label: 'Criação do relatório' });
  }

  if (relatorio.dataEnvio) {
    const prazoInfo = getPrazoEnvioInfo(relatorio);
    const detalheBase = 'Encaminhado para análise e aprovação da diretoria regional';
    eventos.push({
      data: fmt(relatorio.dataEnvio),
      label: 'Envio do defensor para o diretor regional',
      detalhe: prazoInfo?.isForaPrazo
        ? `${detalheBase} (Fora do prazo - prazo final: ${prazoInfo.prazoFinalLabel})`
        : detalheBase,
      badge: prazoInfo?.isForaPrazo
        ? {
            text: 'Fora do prazo',
            title: `Prazo final: ${prazoInfo.prazoFinalLabel}`,
          }
        : undefined,
    });
  }

  if (relatorio.dataReprovacao && !relatorio.dataAprovacao) {
    const detalhe = [
      relatorio.reprovadoPorNome ? `Por: ${relatorio.reprovadoPorNome}` : '',
      relatorio.justificativaReprovacao ? `Justificativa: ${relatorio.justificativaReprovacao}` : '',
    ]
      .filter(Boolean)
      .join(' · ');
    eventos.push({
      data: fmt(relatorio.dataReprovacao),
      label: 'Reprovação pela Diretoria regional ao defensor',
      detalhe: detalhe || undefined,
    });
  }

  if (relatorio.dataAprovacao) {
    eventos.push({
      data: fmt(relatorio.dataAprovacao),
      label: 'Aprovação pelo diretor regional',
      detalhe: relatorio.aprovadoPorNome ? `Aprovado por ${relatorio.aprovadoPorNome}` : undefined,
    });
    eventos.push({
      data: fmt(relatorio.dataAprovacao),
      label: 'Encaminhamento do relatório à Corregedoria',
      detalhe: 'Aguardando aval final da Corregedoria',
    });
  }

  const devDir = relatorio.dados?.devolucaoCorregedoriaAoDiretor as
    | { justificativa?: string; em?: string }
    | undefined;
  if (devDir?.em) {
    eventos.push({
      data: fmt(devDir.em),
      label: 'Devolução pela Corregedoria ao diretor regional',
      detalhe: devDir.justificativa ? `Justificativa: ${devDir.justificativa}` : undefined,
    });
  }

  if (relatorio.dataReprovacao && relatorio.dataAprovacao && relatorio.status === 'reprovado') {
    const detalhe = [
      relatorio.reprovadoPorNome ? `Por: ${relatorio.reprovadoPorNome}` : '',
      relatorio.justificativaReprovacao ? `Justificativa: ${relatorio.justificativaReprovacao}` : '',
    ]
      .filter(Boolean)
      .join(' · ');
    eventos.push({
      data: fmt(relatorio.dataReprovacao),
      label: 'Reprovação pela Corregedoria ao defensor',
      detalhe: detalhe || undefined,
    });
  }

  const envCor = relatorio.dados?.dataEnvioDiretoCorregedoriaDefensor as { em?: string } | undefined;
  if (envCor?.em) {
    eventos.push({
      data: fmt(envCor.em),
      label: 'Reenvio direto à Corregedoria pelo defensor',
      detalhe:
        'Após correções solicitadas pela Corregedoria (sem novo trâmite obrigatório pelo diretor regional)',
    });
  }

  if (relatorio.corregedoriaAprovadoEm) {
    eventos.push({
      data: fmt(relatorio.corregedoriaAprovadoEm),
      label: 'Aval final da Corregedoria — aprovação',
      detalhe: relatorio.corregedoriaAprovadoPorNome
        ? `Registrado por ${relatorio.corregedoriaAprovadoPorNome}`
        : undefined,
    });
  }

  eventos.sort((a, b) => a.data.localeCompare(b.data));
  return eventos;
}
